import Tooltip from 'modules/shared-components/tooltip';
import { TableColumn } from 'modules/shared-components/data-display/table/types';
import {
  BotWhitelistedTokens,
  WhitelistedTokenData,
} from 'api/types/httpsTypes/dex-bot';
import { EthAddress } from 'modules/ethereum/components/EthAddress';
import { Chains } from 'api/types/httpsTypes/d-wallets';
import { formatNumberWithSuffix } from 'utils/FormatNumber';
import { TokenIconWithChain } from 'modules/shared-components/asset/token-icon';
import {
  isTokenSuspicious,
  SuspiciousLabelAlert,
} from '../../../research/components/Wallets/labels';
import {
  ChartBarSquareIcon,
  Cog6ToothIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { useToggle } from 'modules/utils/useToggle';
import ManageTokensModal from './ManageTokensModal';
import Button from 'modules/shared-components/button/button';
import { useHistory } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  dexBotKeys,
  DexBotService,
} from 'api/services/httpServices/DexBotService';
import { useSnackBar } from 'modules/layouts/SnackBar/context';
import { useState } from 'react';
import { screenGte, useMediaQuery } from 'modules/media/use-media-query';

interface ActionProps {
  bots: BotWhitelistedTokens[];
  address: string;
  chain: Chains;
  type?: string;
  symbol: string;
}

export const useRemoveBots = (
  bots: BotWhitelistedTokens[],
  chain: Chains,
  address: string
) => {
  const { addNewMessage } = useSnackBar();
  const { mutate } = useMutation({
    mutationFn: DexBotService.removeTokenFromVolatilityBot,
    onError: () => {
      addNewMessage({
        type: 'error',
        title: 'Error',
        message: 'Some error occurred. Try again.',
      });
    },
  });
  const queryClient = useQueryClient();
  const [isProcessing, setIsProcessing] = useState(false);

  const removeBotWithDelay = (botId: string) => {
    return new Promise<void>((resolve) => {
      setTimeout(() => {
        mutate(
          { botId, chain, address },
          {
            onSuccess: () => resolve(),
            onError: () => resolve(),
          }
        );
      }, 250);
    });
  };

  const handleRemoveAllBots = async () => {
    setIsProcessing(true);
    for (const bot of bots) {
      await removeBotWithDelay(bot.botId);
    }
    setIsProcessing(false);
    void (await queryClient.invalidateQueries(dexBotKeys.all()));
  };

  return { handleRemoveAllBots, isProcessing };
};

function Action({ bots, address, chain, type, symbol }: ActionProps) {
  const isDesktop = useMediaQuery(screenGte.small);
  const [openModal, { open, close }] = useToggle();
  const history = useHistory();
  const { handleRemoveAllBots, isProcessing } = useRemoveBots(
    bots,
    chain,
    address
  );

  return (
    <div>
      {type === 'button' ? (
        <div className="flex lg:flex-row xxs:flex-col xxs:space-y-1 lg:space-y-0 lg:space-x-1 items-center">
          <Button
            type="button"
            onClick={() =>
              history.push({
                pathname: isDesktop
                  ? `/dex/snipe/new/${chain}/${address}`
                  : `/dex/research/${chain}/${address}`,
                state: {
                  from: `${history.location.pathname}${history.location.search}`,
                },
              })
            }
            className="xxs:p-1"
            variant="dexNeutral"
          >
            <ChartBarSquareIcon className="xxs:w-5 xxs:h-5" />
          </Button>
          <Button
            type="button"
            variant="primary"
            className="xxs:py-1 xxs:px-1 text-white"
            onClick={open}
          >
            <Cog6ToothIcon className="w-4 h-4" />
          </Button>

          <Button
            type="button"
            variant="errorDex"
            loading={isProcessing}
            className="xxs:py-1 xxs:px-1 hidden lg:block"
            onClick={() => void handleRemoveAllBots()}
          >
            <TrashIcon className="w-4 h-4" />
          </Button>
        </div>
      ) : (
        <button
          type="button"
          className="m-0 xxs:text-dex-white-secondary hover:text-dex-white"
          onClick={open}
        >
          <Tooltip
            text={
              <ul className="xxs:px-2 xxs:py-0 xxs:m-0">
                {bots.map((bot, index) => {
                  return <li key={index}>{bot.name}</li>;
                })}
              </ul>
            }
          >
            <div className="flex gap-x-1 items-center">
              <div className="lg:hidden xxs:text-xs">Bots:</div>
              {bots.slice(0, 2).map((b, index) => {
                return (
                  <div
                    key={index}
                    className="text-[10px] max-w-[50px] truncate text-nowrap"
                  >
                    {b.name}
                    {index + 1 === bots.length ? '' : ','}
                  </div>
                );
              })}{' '}
              {bots.length > 2 ? (
                <div className="text-[10px] text-nowrap">
                  +{bots.length - 2}
                </div>
              ) : (
                ''
              )}
              <Button
                type="button"
                variant="errorDex"
                loading={isProcessing}
                className="xxs:py-1 xxs:px-1 lg:hidden"
                onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                  e.stopPropagation();
                  void handleRemoveAllBots();
                }}
              >
                <TrashIcon className="w-4 h-4" />
              </Button>
            </div>
          </Tooltip>
        </button>
      )}
      {openModal && (
        <ManageTokensModal
          isBotsLoading={false}
          symbol={symbol}
          address={address}
          chain={chain}
          handleClose={close}
          bots={bots}
        />
      )}
    </div>
  );
}

export const desktopColumns = (): TableColumn<WhitelistedTokenData>[] => {
  return [
    {
      label: (
        <Tooltip text="The Token address, chain, and DEX.">
          <div className="xxs:text-dex-white-secondary hover:text-dex-white">
            Token
          </div>
        </Tooltip>
      ),
      key: 'PAIR',
      component: ({ data }) => (
        <span
          className={
            data.status === 'RUG_PULL' ? 'xxs:text-dex-white-secondary' : ''
          }
        >
          <div className="flex items-center xxs:gap-2">
            <div className=" ">
              <TokenIconWithChain
                hasIcon={data.hasIcon}
                tokenName={data.base}
                chain={data.chain ?? Chains.Ethereum}
                protocol={data.dex}
                symbol={data.base}
                className="w-6 h-6"
                address={data.tokenAddress}
              />
            </div>
            <div className="max-w-[120px] overflow-hidden truncate">
              <span>
                <span className="font-bold text-sm">{data.base}</span>/
                <span className="xxs:text-dex-white-secondary xxs:text-xs">
                  {data.quote}
                </span>
              </span>
              <div>
                <EthAddress
                  address={data.tokenAddress}
                  precision={3}
                  kind="token"
                  className="text-xs"
                  chain={data.chain ?? Chains.Ethereum}
                />
              </div>
            </div>
            <SuspiciousLabelAlert
              alertItems={isTokenSuspicious({
                suspiciousBuy: data.suspiciousBuy,
                rugAddictedBuy: data.rugAddictedBuy,
                scammerBuy: data.scammerBuy,
                freshBuy: data.freshBuy,
                totalBuy: data.totalBuy,
              })}
            />
          </div>
        </span>
      ),
    },
    {
      label: (
        <Tooltip text="The current liquidity in the biggest pool.">
          <div className="xxs:text-dex-white-secondary hover:text-dex-white">
            Liquidity
          </div>
        </Tooltip>
      ),
      key: 'LIQUIDITY',
      component: ({ data }) => (
        <div
          className={
            data.status === 'RUG_PULL' ? 'xxs:text-dex-white-secondary ' : ''
          }
        >
          {data.currentLiquidity ? (
            <Tooltip text="Liquidity in the biggest pool.">
              <div className="text-xs ">
                $
                {formatNumberWithSuffix(data.currentLiquidity, {
                  precision: 2,
                })}
              </div>
            </Tooltip>
          ) : (
            <div className="text-xs">--</div>
          )}
        </div>
      ),
    },
    {
      label: (
        <Tooltip text="The current fully diluted Market Capitalization.">
          <div className="xxs:text-dex-white-secondary hover:text-dex-white">
            Market Cap
          </div>
        </Tooltip>
      ),
      key: 'MARKET_CAP',
      component: ({ data }) => (
        <div
          className={
            data.status === 'RUG_PULL' ? 'xxs:text-dex-white-secondary ' : ''
          }
        >
          {data.currentMarketCap ? (
            <Tooltip text="Fully diluted Market Cap.">
              <div className=" text-xs ">
                $
                {formatNumberWithSuffix(data.currentMarketCap, {
                  precision: 2,
                })}
              </div>
            </Tooltip>
          ) : (
            <div className="text-xs">--</div>
          )}
        </div>
      ),
    },
    {
      label: (
        <Tooltip text="The total trading volume in the last hour.">
          <div className="xxs:text-dex-white-secondary hover:text-dex-white">
            1H Volume
          </div>
        </Tooltip>
      ),
      key: '1H_VOLUME',
      component: ({ data }) => (
        <div
          className={
            data.status === 'RUG_PULL' ? 'xxs:text-dex-white-secondary ' : ''
          }
        >
          {data.oneHourVolume ? (
            <div className="flex items-center space-x-1 text-xs">
              <div>
                ${formatNumberWithSuffix(data.oneHourVolume, { precision: 2 })}
              </div>
            </div>
          ) : (
            <div className="text-xs">--</div>
          )}
        </div>
      ),
    },
    {
      label: (
        <Tooltip text="The total trading volume in the last 6 hours.">
          <div className="xxs:text-dex-white-secondary hover:text-dex-white">
            6H Volume
          </div>
        </Tooltip>
      ),
      key: '&H_VOLUME',
      component: ({ data }) => (
        <div
          className={
            data.status === 'RUG_PULL' ? 'xxs:text-dex-white-secondary ' : ''
          }
        >
          {data.sixHoursVolume ? (
            <div className="flex items-center space-x-1 text-xs">
              <div>
                ${formatNumberWithSuffix(data.sixHoursVolume, { precision: 2 })}
              </div>
            </div>
          ) : (
            <div className="text-xs">--</div>
          )}
        </div>
      ),
    },

    {
      label: (
        <Tooltip text="The total trading volume in the last 24 hours.">
          <div className="xxs:text-dex-white-secondary hover:text-dex-white">
            24H Volume
          </div>
        </Tooltip>
      ),
      key: '24H_VOLUME',
      component: ({ data }) => (
        <div
          className={
            data.status === 'RUG_PULL' ? 'xxs:text-dex-white-secondary ' : ''
          }
        >
          {data.twentyFourHoursVolume ? (
            <div className="flex items-center space-x-1 text-xs">
              <div>
                $
                {formatNumberWithSuffix(data.twentyFourHoursVolume, {
                  precision: 2,
                })}
              </div>
            </div>
          ) : (
            <div className="text-xs">--</div>
          )}
        </div>
      ),
    },
    {
      label: (
        <Tooltip text="The bots where the pair is whitelisted.">
          <div className="xxs:text-dex-white-secondary hover:text-dex-white">
            Bots
          </div>
        </Tooltip>
      ),
      key: 'BOTS',
      component: ({ data }) => (
        <Action
          symbol={data.base}
          bots={data.bots}
          address={data.tokenAddress}
          chain={data.chain}
        />
      ),
    },
    {
      label: (
        <Tooltip text="Manage your pairs.">
          <div className="xxs:text-dex-white-secondary hover:text-dex-white">
            Action
          </div>
        </Tooltip>
      ),
      key: 'ACTION',
      component: ({ data }) => (
        <Action
          symbol={data.base}
          type="button"
          bots={data.bots}
          address={data.tokenAddress}
          chain={data.chain}
        />
      ),
    },
  ];
};

export const mobileColumns = (): TableColumn<WhitelistedTokenData>[] => {
  return [
    {
      key: 'PAIR',
      component: ({ data }) => (
        <div
          className={
            data.status === 'RUG_PULL' ? 'xxs:text-dex-white-secondary' : ''
          }
        >
          <div className="flex justify-between items-center ">
            <div className="">
              <div className="flex items-center xxs:gap-2">
                <div className="relative">
                  <TokenIconWithChain
                    hasIcon={data.hasIcon}
                    tokenName={data.base}
                    chain={data.chain ?? Chains.Ethereum}
                    protocol={data.dex}
                    symbol={data.base}
                    className="w-6 h-6"
                    address={data.tokenAddress}
                  />
                </div>
                <div className="max-w-[120px] overflow-hidden truncate">
                  <div>
                    <span className=" text-sm">{data.base}</span>/
                    <span className="xxs:text-dex-white-secondary xxs:text-xs">
                      {data.quote}
                    </span>
                  </div>
                  <div>
                    <EthAddress
                      className="text-xs"
                      address={data.tokenAddress}
                      precision={3}
                      kind="token"
                      chain={data.chain ?? Chains.Ethereum}
                    />
                  </div>
                </div>
                <SuspiciousLabelAlert
                  alertItems={isTokenSuspicious({
                    suspiciousBuy: data.suspiciousBuy,
                    rugAddictedBuy: data.rugAddictedBuy,
                    scammerBuy: data.scammerBuy,
                    freshBuy: data.freshBuy,
                    totalBuy: data.totalBuy,
                  })}
                />
              </div>
              <Action
                symbol={data.base}
                bots={data.bots}
                address={data.tokenAddress}
                chain={data.chain}
              />
            </div>

            <div className="xxs:flex xxs:space-x-4 xxs:items-center">
              <div className="xxs:space-y-0.5">
                <div className=" flex  xxs:space-x-2 xxs:justify-end">
                  {data.twentyFourHoursVolume ? (
                    <div className="flex space-x-1 text-base">
                      <div>
                        $
                        {formatNumberWithSuffix(
                          data.twentyFourHoursVolume ?? 0,
                          {
                            precision: 2,
                          }
                        )}
                      </div>
                      <div className="xxs:text-[10px] xxs:pt-0.5">24h</div>
                    </div>
                  ) : (
                    <div className="text-xs">--</div>
                  )}
                  {data.oneHourVolume ? (
                    <div className="flex space-x-1 text-xs xxs:pt-1 xxs:justify-end">
                      <div className="xxs:pt-0.5">
                        $
                        {formatNumberWithSuffix(data.oneHourVolume ?? 0, {
                          precision: 2,
                        })}
                      </div>
                      <div className="xxs:text-[10px] xxs:pt-0.5">1h</div>
                    </div>
                  ) : (
                    <div className="text-xs">--</div>
                  )}
                </div>

                <div className="space-x-1 flex items-center text-xs justify-end ">
                  {data.currentMarketCap && (
                    <div>
                      🧢 $
                      {formatNumberWithSuffix(data.currentMarketCap ?? 0, {
                        precision: 2,
                      })}
                    </div>
                  )}
                  <div>|</div>
                  {data.currentLiquidity && (
                    <div>
                      💧$
                      {formatNumberWithSuffix(data.currentLiquidity ?? 0, {
                        precision: 2,
                      })}
                    </div>
                  )}
                </div>
              </div>
              <Action
                type="button"
                bots={data.bots}
                address={data.tokenAddress}
                chain={data.chain}
                symbol={data.base}
              />
            </div>
          </div>
        </div>
      ),
    },
  ];
};
